import React from "react";
import { MdCheck, MdOutlineClose } from "react-icons/md";
import { IQuotationRow } from "./quotationListTypes";
import styles from "../../asset/styles/quotation/quotation.module.scss";
import {
    formatPrice,
    formatQuotationListTravelDates,
    canDuplicateQuotationListRow,
    canDeleteQuotationListRow,
    canMarkConfirmedOrLostQuotationListRow,
} from "./quotationListUtil";
import QuotationStatusPill from "../quotationStatusBadge/QuotationStatusPill";
import { quotationStatusVariantFromLabel } from "../quotation/quotationStatusVariant";
import IconsColor from "../../util/iconsColor";
import { TableColumn } from "../table/table";
import CustomizedMenu, { MenuItemData } from "../muiMenu/muiMenu";
import QuotationAgencyContactHover from "./QuotationAgencyContactHover";

const MARK_CONFIRMED_COLOR = "#166534"
const MARK_LOST_COLOR = "#991B1B"

const MarkConfirmedLabel: React.FC<{ iconPosition: "prefix" | "suffix" }> = ({ iconPosition }) => (
    <span className={styles.statusActionLabel}>
        {iconPosition === "prefix" && <MdCheck size={20} color={MARK_CONFIRMED_COLOR} />}
        <span style={{ color: MARK_CONFIRMED_COLOR }}>Mark Confirmed</span>
        {iconPosition === "suffix" && <MdCheck size={20} color={MARK_CONFIRMED_COLOR} />}
    </span>
)

const MarkLostLabel: React.FC<{ iconPosition: "prefix" | "suffix" }> = ({ iconPosition }) => (
    <span className={styles.statusActionLabel}>
        {iconPosition === "prefix" && <MdOutlineClose size={20} color={MARK_LOST_COLOR} />}
        <span style={{ color: MARK_LOST_COLOR }}>Mark Lost</span>
        {iconPosition === "suffix" && <MdOutlineClose size={20} color={MARK_LOST_COLOR} />}
    </span>
)

export const getColumnsArray = (
    handleDuplicateClick: (row: IQuotationRow, event: React.MouseEvent) => Promise<void>,
    handleDeleteClick: (row: IQuotationRow, event: React.MouseEvent) => void,
    handleFollowUpsChange: (quotationId: string, followUps: IQuotationRow["follow_ups"]) => void,
    handleMarkConfirmedClick: (row: IQuotationRow, event: React.MouseEvent) => void,
    handleMarkLostClick: (row: IQuotationRow, event: React.MouseEvent) => void,
    openActivityHistoryForRow: (row: IQuotationRow) => void,
    markConfirmedLostStatusIds: number[],
    draftStatusId: number,
): TableColumn<IQuotationRow>[] => {
    return [
        {
            key: "sta_code",
            label: "ID",
            width: "10%",
            align: "left",
            render: (value: string) => <span className={styles.quotationId}>{value}</span>,
        },
        {
            key: "source",
            label: "Source",
            width: "8%",
            align: "left",
        },
        {
            key: "quotation_name",
            label: "Quotation Name",
            width: "14%",
            align: "left",
        },
        {
            key: "agency_name",
            label: "Agency",
            width: "14%",
            align: "left",
            render: (_value: string, row: IQuotationRow) => (
                <QuotationAgencyContactHover
                    agencyName={String(row.agency_name || "").trim()}
                    agentName={String(row.agent_name || "").trim()}
                    phone={String(row.contact_info?.phone || "").trim()}
                    email={String(row.contact_info?.email || "").trim()}
                />
            ),
        },
        {
            key: "cities_covered",
            label: "Destination",
            width: "10%",
            align: "left",
        },
        {
            key: "travel_dates",
            label: "Travel Dates",
            width: "10%",
            align: "left",
            render: (_value: string, row: IQuotationRow) => (
                <span className={styles.dateValue}>{formatQuotationListTravelDates(row.start_date, row.end_date)}</span>
            )
        },
        {
            key: "total_pax",
            label: "Pax",
            width: "10%",
            align: "center",
            render: (value: number) => <span className={styles.paxBadge}>{value}</span>,
        },
        {
            key: "total_price_usd",
            label: "Price ($)",
            width: "10%",
            align: "left",
            render: (value: number) => <span className={styles.priceValue}>{formatPrice(value)}</span>,
        },
        {
            key: "status",
            label: "Status",
            width: "10%",
            align: "left",
            render: (value: IQuotationRow["status"], row: IQuotationRow) => {
                return (
                    <QuotationStatusPill
                        quotation_id={row.quotation_id}
                        sta_code={row.sta_code}
                        follow_ups={row.follow_ups}
                        status_id={row.status_id}
                        status_name={value}
                        variant={quotationStatusVariantFromLabel(value)}
                        flagViewOnly={true}
                        flagShowActivityHistory={true}
                        flagShowListStatusMeta={true}
                        listStatusMeta={{
                            confirmedOn: row.quotation_status_changed_at,
                            nextFollowUpDate: row.next_follow_up_date,
                            lostReason: row.remark,
                        }}
                        onActivityHistoryOpen={() => openActivityHistoryForRow(row)}
                        onFollowUpsChange={handleFollowUpsChange}
                    />
                )
            }
        },
        {
            key: "created_by",
            label: "Created By",
            width: "10%",
            align: "left",
        },
        {
            key: "actions",
            label: "Actions",
            width: "5%",
            align: "left",
            render: (_: any, row: IQuotationRow) => (
                <QuotationListActionDataRenderer
                    row={row}
                    handleDuplicateClick={handleDuplicateClick}
                    handleDeleteClick={handleDeleteClick}
                    handleMarkConfirmedClick={handleMarkConfirmedClick}
                    handleMarkLostClick={handleMarkLostClick}
                    openActivityHistoryForRow={openActivityHistoryForRow}
                    markConfirmedLostStatusIds={markConfirmedLostStatusIds}
                    draftStatusId={draftStatusId}
                    value={_}
                />
            ),
        }
    ]
}

interface IQuotationListActionDataRendererProps {
    value: any;
    row: IQuotationRow;
    handleDuplicateClick: (row: IQuotationRow, event: React.MouseEvent) => Promise<void>,
    handleDeleteClick: (row: IQuotationRow, event: React.MouseEvent) => void
    handleMarkConfirmedClick: (row: IQuotationRow, event: React.MouseEvent) => void
    handleMarkLostClick: (row: IQuotationRow, event: React.MouseEvent) => void
    openActivityHistoryForRow: (row: IQuotationRow) => void
    markConfirmedLostStatusIds: number[]
    draftStatusId: number
}

const QuotationListActionDataRenderer = (props: IQuotationListActionDataRendererProps) => {
    const {
        handleDuplicateClick,
        row,
        handleDeleteClick,
        handleMarkConfirmedClick,
        handleMarkLostClick,
        openActivityHistoryForRow,
        markConfirmedLostStatusIds,
        draftStatusId,
    } = props;

    const menuItems: MenuItemData[] = []

    if (canDeleteQuotationListRow(row, draftStatusId)) {
        menuItems.push({
            label: <div style={{ display: "flex", alignItems: "center", color: "#991B1B" }}><IconsColor iconName={"deleteIcon"} width={"22px"} height={"18px"} strokeColor={"#991B1B"} />&nbsp;Delete Quotation</div>,
            onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                handleDeleteClick(row, e)
            },
        })
    }

    if (canMarkConfirmedOrLostQuotationListRow(row, markConfirmedLostStatusIds)) {
        menuItems.push(
            {
                label: <MarkConfirmedLabel iconPosition="prefix" />,
                onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                    handleMarkConfirmedClick(row, e)
                },
            },
            {
                label: <MarkLostLabel iconPosition="prefix" />,
                onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                    handleMarkLostClick(row, e)
                },
            },
        )
    }

    if (canDuplicateQuotationListRow(row)) {
        menuItems.push({
            label: <div style={{ display: "flex", alignItems: "center" }}><IconsColor iconName={"duplicateIcon"} width={"22px"} height={"18px"} />&nbsp;Duplicate Quotation</div>,
            onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                handleDuplicateClick(row, e)
            },
        })
    }

    menuItems.push({
        label: (
            <div style={{ display: "flex", alignItems: "center" }}>
                {/* <FiClock className={styles.activityHistoryIcon} color="#64748B" strokeWidth={2.5} /> */}
                <IconsColor iconName={"activityHistoryIcon"} width={"22px"} height={"18px"} strokeColor="64748B" />
                &nbsp;Show Activity History
            </div>
        ),
        onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
            e.stopPropagation()
            openActivityHistoryForRow(row)
        },
    })

    return (
        <span onClick={(e) => { e.stopPropagation(); }}>
            <CustomizedMenu
                buttonText=""
                menuItems={menuItems}
                buttonVariant="text"
                buttonStyle={{ padding: '0px 8px 0 0', minWidth: '0px' }}
                buttonEndIcon={<IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />}
            />
        </span>
    )
}

